import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  GeoPoint,
  Timestamp,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  DocumentData,
  writeBatch,
  deleteDoc,
  onSnapshot,
  DocumentReference,
  increment as firestoreIncrement,
  arrayUnion as firestoreArrayUnion,
  arrayRemove as firestoreArrayRemove,
  setDoc,
  limit,
  orderBy
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { UserProfile, UserSale, ParkingType, Coordinates } from '../types/user';
import { DashboardMetrics } from '../types/admin';
import { Review } from '../types/social';
import { calculateDistance, reverseGeocode } from '../services/mapbox';

// Initialize Firebase with environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Types
export interface ListingsQueryParams {
  location?: string;
  categories?: string[];
  startDate?: string;  // ISO date string
  endDate?: string;    // ISO date string
  parkingType?: ParkingType;
  radius?: number;
  searchQuery?: string;
  distance?: number;
  limit?: number;
  coordinates?: Coordinates;
  city?: string;
}

export interface ListingsResponse {
  listings: UserSale[];
  lastDoc?: DocumentSnapshot;
}

// Auth functions
export const signUp = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  location: string
): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`
    });

    const userDoc = {
      id: user.uid,
      firstName,
      lastName,
      email,
      location,
      joinDate: new Date().toISOString(),
      tier: 'free',
      isVerified: false,
      savedSales: [],
      stats: {
        totalSales: 0,
        activeSales: 0,
        completedSales: 0,
        totalRevenue: 0,
        averageRating: 0,
        reviewCount: 0,
        followers: 0,
        following: 0
      },
      preferences: {
        emailNotifications: true,
        pushNotifications: false,
        radius: 25,
        defaultCategories: [],
        privateProfile: false
      }
    };

    await setDoc(doc(db, 'users', user.uid), userDoc);

    return user;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

export const signIn = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

export const logOut = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Admin functions
export const adminSignIn = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const isAdmin = await verifyAdminRole(userCredential.user);
    if (!isAdmin) {
      throw new Error('User is not an admin');
    }
    return userCredential.user;
  } catch (error) {
    console.error('Error signing in as admin:', error);
    throw error;
  }
};

export const verifyAdminRole = async (user: User): Promise<boolean> => {
  if (!user) return false;
  
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    return userSnap.exists() && userSnap.data().role === 'admin';
  } catch (error) {
    console.error('Error verifying admin role:', error);
    return false;
  }
};

export const subscribeToMetrics = (callback: (metrics: DashboardMetrics) => void) => {
  const metricsRef = collection(db, 'metrics');
  const docRef = doc(metricsRef, 'dashboard');
  
  return onSnapshot(docRef, (snapshot: DocumentSnapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data() as DashboardMetrics);
    }
  });
};

// User Profile functions
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data() as UserProfile;
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId: string, updates: Partial<UserProfile>): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), updates);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Review functions
export const addReview = async (saleId: string, userId: string, rating: number): Promise<void> => {
  try {
    const batch = writeBatch(db);
    const reviewsRef = collection(db, 'reviews');
    const saleRef = doc(db, 'sales', saleId);
    const userRef = doc(db, 'users', userId);
    const now = new Date();

    // Check if user has already reviewed
    const existingReviewQuery = query(
      reviewsRef,
      where('saleId', '==', saleId),
      where('userId', '==', userId)
    );
    const existingReviews = await getDocs(existingReviewQuery);

    if (!existingReviews.empty) {
      // Update existing review
      const existingReview = existingReviews.docs[0];
      const oldRating = existingReview.data().rating;
      
      batch.update(existingReview.ref, {
        rating,
        updatedAt: now
      });

      // Update seller's stats
      batch.update(userRef, {
        'stats.averageRating': firestoreIncrement((rating - oldRating) / existingReviews.size)
      });
    } else {
      // Create new review
      const newReview: Review = {
        id: doc(reviewsRef).id,
        saleId,
        userId,
        rating,
        createdAt: now
      };

      batch.set(doc(reviewsRef, newReview.id), newReview);

      // Update seller's stats
      batch.update(userRef, {
        'stats.reviewCount': firestoreIncrement(1),
        'stats.averageRating': firestoreIncrement(rating)
      });
    }

    await batch.commit();
  } catch (error) {
    console.error('Error adding review:', error);
    throw error;
  }
};

export const getReviewsForSale = async (saleId: string): Promise<Review[]> => {
  try {
    const q = query(
      collection(db, 'reviews'),
      where('saleId', '==', saleId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      createdAt: doc.data().createdAt.toDate()
    })) as Review[];
  } catch (error) {
    console.error('Error getting reviews:', error);
    throw error;
  }
};

// Yard Sale functions
export const createYardSale = async (userId: string, saleData: Partial<UserSale>): Promise<UserSale> => {
  try {
    const batch = writeBatch(db);
    const salesRef = collection(db, 'sales');
    const userRef = doc(db, 'users', userId);
    const now = new Date();

    // Extract city from location
    const city = saleData.location?.split(',')[0].trim() || '';

    // Create the new sale document
    const newSale: UserSale = {
      id: '', // Will be set after creation
      userId,
      title: saleData.title || '',
      description: saleData.description || '',
      date: saleData.date || '',
      time: saleData.time || { start: '', end: '' },
      location: saleData.location || '',
      city,
      coordinates: saleData.coordinates,
      status: 'upcoming',
      categories: saleData.categories || [],
      images: saleData.images || [],
      stats: {
        views: 0,
        saves: 0
      },
      featured: false,
      parkingType: saleData.parkingType || 'none',
      createdAt: now,
      updatedAt: now
    };

    // Create a new document reference with auto-generated ID
    const newSaleRef = doc(salesRef);
    newSale.id = newSaleRef.id;

    // Add the sale to the batch
    batch.set(newSaleRef, {
      ...newSale,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      // Store coordinates as GeoPoint if available
      ...(newSale.coordinates && {
        geopoint: new GeoPoint(newSale.coordinates.lat, newSale.coordinates.lng)
      })
    });

    // Update user's stats
    batch.update(userRef, {
      'stats.activeSales': firestoreIncrement(1),
      'stats.totalSales': firestoreIncrement(1)
    });

    // Commit the batch
    await batch.commit();

    return newSale;
  } catch (error) {
    console.error('Error creating yard sale:', error);
    throw error;
  }
};

export const getYardSale = async (saleId: string): Promise<UserSale | null> => {
  try {
    const saleDoc = await getDoc(doc(db, 'sales', saleId));
    if (saleDoc.exists()) {
      const data = saleDoc.data();
      // Convert GeoPoint to coordinates if available
      const geopoint = data.geopoint;
      const coordinates = geopoint ? {
        lat: geopoint.latitude,
        lng: geopoint.longitude
      } : undefined;

      return {
        ...data,
        coordinates,
        city: data.city || data.location?.split(',')[0].trim() || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as UserSale;
    }
    return null;
  } catch (error) {
    console.error('Error getting yard sale:', error);
    throw error;
  }
};

export const updateYardSale = async (saleId: string, updates: Partial<UserSale>): Promise<void> => {
  try {
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now(),
      // Update GeoPoint if coordinates are provided
      ...(updates.coordinates && {
        geopoint: new GeoPoint(updates.coordinates.lat, updates.coordinates.lng)
      }),
      // Update city if location is updated
      ...(updates.location && {
        city: updates.location.split(',')[0].trim()
      })
    };
    
    await updateDoc(doc(db, 'sales', saleId), updateData);
  } catch (error) {
    console.error('Error updating yard sale:', error);
    throw error;
  }
};

export const getUserYardSales = async (userId: string): Promise<UserSale[]> => {
  try {
    const q = query(collection(db, 'sales'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        city: data.city || data.location?.split(',')[0].trim() || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as UserSale;
    });
  } catch (error) {
    console.error('Error getting user yard sales:', error);
    throw error;
  }
};

export const getYardSaleListings = async (params: ListingsQueryParams): Promise<ListingsResponse> => {
  try {
    const salesRef = collection(db, 'sales');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = today.toISOString().split('T')[0];
    console.log('Initial query params:', params);

    // Start with active and upcoming sales
    let q = query(salesRef, where('status', 'in', ['active', 'upcoming']));

    // Add city filter if provided
    if (params.city) {
      q = query(q, where('city', '==', params.city));
    }

    // Add date filtering in the query
    if (params.startDate) {
      q = query(q, where('date', '>=', params.startDate));
    } else {
      // If no start date specified, filter for future dates only
      q = query(q, where('date', '>=', todayISO));
    }

    if (params.endDate) {
      q = query(q, where('date', '<=', params.endDate));
    }

    // Add ordering by date
    q = query(q, orderBy('date', 'asc'));

    const querySnapshot = await getDocs(q);
    
    let listings: UserSale[] = querySnapshot.docs.map(doc => {
      const data = doc.data();
      const geopoint = data.geopoint;
      const coordinates = geopoint ? {
        lat: geopoint.latitude,
        lng: geopoint.longitude
      } : undefined;

      return {
        id: doc.id,
        userId: data.userId,
        title: data.title,
        description: data.description,
        date: data.date,
        time: {
          start: data.time.start,
          end: data.time.end
        },
        location: data.location,
        city: data.city || data.location?.split(',')[0].trim() || '',
        coordinates,
        status: data.status,
        categories: data.categories || [],
        images: data.images || [],
        stats: {
          views: data.stats?.views || 0,
          saves: data.stats?.saves || 0
        },
        featured: data.featured || false,
        parkingType: data.parkingType,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      };
    });

    // Filter based on location text search
    if (params.location && typeof params.location === 'string') {
      // Skip location filtering if it's a coordinate string
      if (!params.location.includes(',') || isNaN(parseFloat(params.location.split(',')[0]))) {
        const searchTerms = params.location.toLowerCase().split(',').map(term => term.trim());
        listings = listings.filter(listing => {
          if (!listing.location) return false;
          const locationParts = listing.location.toLowerCase().split(',').map(part => part.trim());
          
          // Match each search term against location parts
          // Each term must be contained within at least one part
          return searchTerms.every(term => 
            locationParts.some(part => part.includes(term))
          );
        });
      }
    }

    // Apply category filters
    if (params.categories?.length) {
      listings = listings.filter(listing =>
        listing.categories.some(cat => params.categories!.includes(cat))
      );
    }

    // Sort by featured status after date sorting
    listings = listings.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return 0;
    });

    // Apply limit if specified
    if (params.limit) {
      listings = listings.slice(0, params.limit);
    }

    return {
      listings,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1]
    };

  } catch (error) {
    console.error('Error getting yard sale listings:', error);
    throw error;
  }
};

export const deleteYardSale = async (saleId: string, userId: string) => {
  try {
    // Verify the user owns the sale
    const saleRef = doc(db, 'sales', saleId);
    const saleDoc = await getDoc(saleRef);
    
    if (!saleDoc.exists() || saleDoc.data().userId !== userId) {
      throw new Error('Unauthorized to delete this sale');
    }

    // Delete the sale
    await deleteDoc(saleRef);
    return true;
  } catch (error) {
    console.error('Error deleting yard sale:', error);
    throw error;
  }
};
